import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ArticlePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [articleUrl, setArticleUrl] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [fullArticleUrl, setFullArticleUrl] = useState('');

  useEffect(() => {
    fetchArticle();
    fetchRelatedArticles();
    // Set URLs for sharing after component mounts
    if (typeof window !== 'undefined') {
      const currentUrl = window.location.href;
      const origin = window.location.origin;
      const articlePath = `/article/${id}`;
      const fullUrl = `${origin}${articlePath}`;
      
      setArticleUrl(currentUrl);
      setShareUrl(currentUrl);
      setFullArticleUrl(fullUrl);
    }
  }, [id]);

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/articles/${id}`);
      setArticle(response.data);
      setLoading(false);
    } catch (err) {
      setError('Article not found');
      setLoading(false);
    }
  };

  const fetchRelatedArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles/trending/list?limit=5`);
      setRelatedArticles(response.data.filter(a => a.id !== id)); // Exclude current article
    } catch (err) {
      console.error('Error fetching related articles:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-light mb-4">Article Not Found</h1>
          <p className="text-gray-400 mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/" className="px-8 py-3 bg-white text-black font-bold hover:bg-gray-200 transition-colors">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Dynamic Meta Tags for Social Sharing */}
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{article.title} | John Ghatti</title>
        <meta name="title" content={`${article.title} | John Ghatti`} />
        <meta name="description" content={article.excerpt || article.content.substring(0, 160)} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={fullArticleUrl || shareUrl} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt || article.content.substring(0, 160)} />
        <meta property="og:image" content={article.featured_image} />
        <meta property="og:image:secure_url" content={article.featured_image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:site_name" content="John Ghatti" />
        <meta property="article:published_time" content={article.created_at} />
        <meta property="article:author" content={article.author} />
        <meta property="article:section" content={article.category} />
        {article.tags && article.tags.map((tag, index) => (
          <meta key={index} property="article:tag" content={tag} />
        ))}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={fullArticleUrl || shareUrl} />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt || article.content.substring(0, 160)} />
        <meta name="twitter:image" content={article.featured_image} />
        <meta name="twitter:creator" content="@johnghatti" />
        
        {/* WhatsApp specific (uses Open Graph) */}
        <meta property="og:image:alt" content={article.title} />
        
        {/* Additional SEO */}
        <meta name="author" content={article.author} />
        <meta name="keywords" content={article.tags ? article.tags.join(', ') : 'forex, trading, john ghatti'} />
        <link rel="canonical" href={fullArticleUrl || shareUrl} />
      </Helmet>
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-70 transition-opacity">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-200">
              <img
                src="https://customer-assets.emergentagent.com/job_b9ac4c36-714b-45c0-9160-f047c039178c/artifacts/vio7h01p_image.png"
                alt="John Ghatti"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-xl font-semibold tracking-tight">JOHN GHATTI</span>
          </Link>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm text-gray-600 hover:text-black transition-colors"
          >
            ← Back
          </button>
        </div>
      </header>

      {/* Main Layout: Forbes Style with Sidebar */}
      <div className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Sticky Floating Share Bar - Left Side (Desktop Only) */}
            <div className="hidden lg:block fixed left-8 top-1/2 -translate-y-1/2 z-40">
              <div className="flex flex-col gap-3 bg-white rounded-lg shadow-lg p-3 border border-gray-200">
                <span className="text-xs font-bold text-gray-500 text-center mb-1">SHARE</span>
                
                {/* WhatsApp */}
                <a
                  href={shareUrl ? `https://wa.me/?text=${encodeURIComponent(article.title + ' - ' + shareUrl)}` : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors group"
                  title="WhatsApp"
                  onClick={(e) => {
                    if (!articleUrl) e.preventDefault();
                  }}
                >
                  <svg className="w-6 h-6 text-[#25D366] group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </a>

                {/* Facebook */}
                <a
                  href={shareUrl ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors group"
                  title="Facebook"
                  onClick={(e) => {
                    if (!articleUrl) e.preventDefault();
                  }}
                >
                  <svg className="w-6 h-6 text-[#1877F2] group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>

                {/* Twitter */}
                <a
                  href={shareUrl ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(shareUrl)}&via=johnghatti` : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors group"
                  title="Twitter"
                  onClick={(e) => {
                    if (!articleUrl) e.preventDefault();
                  }}
                >
                  <svg className="w-6 h-6 text-black group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>

                {/* LinkedIn */}
                <a
                  href={shareUrl ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors group"
                  title="LinkedIn"
                  onClick={(e) => {
                    if (!articleUrl) e.preventDefault();
                  }}
                >
                  <svg className="w-6 h-6 text-[#0A66C2] group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>

                {/* Telegram */}
                <a
                  href={shareUrl ? `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}` : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors group"
                  title="Telegram"
                  onClick={(e) => {
                    if (!articleUrl) e.preventDefault();
                  }}
                >
                  <svg className="w-6 h-6 text-[#26A5E4] group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                </a>

                {/* Copy Link */}
                <button
                  onClick={() => {
                    if (shareUrl) {
                      navigator.clipboard.writeText(shareUrl);
                      alert('Link copied!');
                    } else {
                      alert('Please wait...');
                    }
                  }}
                  className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors group"
                  title="Copy Link"
                >
                  <svg className="w-6 h-6 text-gray-600 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Main Article Content - 8 columns */}
            <article className="lg:col-span-8">
              {/* Category Badge */}
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-black text-white text-xs font-bold tracking-wider uppercase">
                  {article.category}
                </span>
              </div>

              {/* Title - Forbes Style */}
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6 text-gray-900">
                {article.title}
              </h1>

              {/* Excerpt/Subtitle */}
              {article.excerpt && (
                <p className="text-xl text-gray-600 leading-relaxed mb-6 font-light">
                  {article.excerpt}
                </p>
              )}

              {/* Meta Info - Forbes Style */}
              <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-gray-500 mb-8 pb-6 border-b border-gray-200">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full overflow-hidden">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_b9ac4c36-714b-45c0-9160-f047c039178c/artifacts/vio7h01p_image.png"
                        alt={article.author}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="font-semibold text-black">{article.author}</span>
                  </div>
                  <span className="hidden sm:inline">•</span>
                  <span>{new Date(article.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  <span className="hidden sm:inline">•</span>
                  <span>{article.view_count} views</span>
                </div>

                {/* Share Buttons */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-700 mr-2">SHARE:</span>
                  
                  {/* WhatsApp */}
                  <a
                    href={shareUrl ? `https://wa.me/?text=${encodeURIComponent(article.title + ' - ' + shareUrl)}` : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    title="Share on WhatsApp"
                    onClick={(e) => {
                      if (!articleUrl) {
                        e.preventDefault();
                        alert('Please wait...');
                      }
                    }}
                  >
                    <svg className="w-5 h-5 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </a>

                  {/* Facebook */}
                  <a
                    href={shareUrl ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    title="Share on Facebook"
                    onClick={(e) => {
                      if (!articleUrl) e.preventDefault();
                    }}
                  >
                    <svg className="w-5 h-5 text-[#1877F2]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>

                  {/* Twitter */}
                  <a
                    href={shareUrl ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(shareUrl)}&via=johnghatti` : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    title="Share on Twitter"
                    onClick={(e) => {
                      if (!articleUrl) e.preventDefault();
                    }}
                  >
                    <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>

                  {/* LinkedIn */}
                  <a
                    href={shareUrl ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    title="Share on LinkedIn"
                    onClick={(e) => {
                      if (!articleUrl) e.preventDefault();
                    }}
                  >
                    <svg className="w-5 h-5 text-[#0A66C2]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>

                  {/* Telegram */}
                  <a
                    href={shareUrl ? `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}` : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    title="Share on Telegram"
                    onClick={(e) => {
                      if (!articleUrl) e.preventDefault();
                    }}
                  >
                    <svg className="w-5 h-5 text-[#26A5E4]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                  </a>

                  {/* Copy Link */}
                  <button
                    onClick={() => {
                      if (shareUrl) {
                        navigator.clipboard.writeText(shareUrl);
                        alert('Link copied to clipboard!');
                      } else {
                        alert('Please wait...');
                      }
                    }}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    title="Copy link"
                  >
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Featured Image - Full Width */}
              {article.featured_image && (
                <div className="mb-10">
                  <img
                    src={article.featured_image}
                    alt={article.title}
                    className="w-full h-auto"
                  />
                </div>
              )}

              {/* Article Content - Forbes Typography */}
              <div className="prose prose-lg max-w-none">
                <div className="text-gray-800 leading-relaxed whitespace-pre-line text-lg forbes-content">
                  {article.content}
                </div>
              </div>

              {/* Tags */}
              {article.tags && article.tags.length > 0 && (
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gray-100 text-xs text-gray-700 hover:bg-gray-200 transition-colors"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA Section - Forbes Style */}
              <div className="mt-12 p-8 bg-black text-white">
                <h3 className="text-2xl font-bold mb-3">Ready to Experience Kasango AI Pro?</h3>
                <p className="text-gray-300 mb-6">Join 1,250+ traders worldwide and start your automated trading journey today.</p>
                <Link
                  to="/cart"
                  className="inline-block px-8 py-3 bg-[#00FF7F] text-black font-bold hover:bg-[#00DD6F] transition-colors"
                >
                  GET KASANGO AI PRO - $1,450
                </Link>
              </div>
            </article>

            {/* Sidebar - Forbes Style - 4 columns */}
            <aside className="lg:col-span-4">
              <div className="sticky top-24">
                {/* Popular/Trending Articles */}
                <div className="bg-gray-50 border border-gray-200 p-6">
                  <h3 className="text-lg font-bold mb-6 pb-3 border-b border-gray-300">POPULAR ARTICLES</h3>
                  
                  <div className="space-y-6">
                    {relatedArticles.map((relatedArticle, index) => (
                      <Link
                        key={relatedArticle.id}
                        to={`/article/${relatedArticle.id}`}
                        className="block group"
                      >
                        {/* Article Preview */}
                        <div className="flex gap-3">
                          <span className="text-2xl font-bold text-gray-300 flex-shrink-0">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <div className="flex-1">
                            {relatedArticle.featured_image && (
                              <div className="mb-2">
                                <img
                                  src={relatedArticle.featured_image}
                                  alt={relatedArticle.title}
                                  className="w-full h-32 object-cover"
                                />
                              </div>
                            )}
                            <span className="inline-block px-2 py-0.5 bg-black text-white text-xs font-bold mb-2">
                              {relatedArticle.category.toUpperCase()}
                            </span>
                            <h4 className="font-bold text-sm leading-snug mb-1 group-hover:underline">
                              {relatedArticle.title}
                            </h4>
                            <p className="text-xs text-gray-600">
                              {relatedArticle.author} • {relatedArticle.view_count} views
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Subscribe CTA */}
                <div className="mt-6 bg-black text-white p-6">
                  <h3 className="text-xl font-bold mb-2">Stay Updated</h3>
                  <p className="text-sm text-gray-300 mb-4">Get the latest insights on forex AI and trading strategies.</p>
                  <a
                    href="https://wa.me/27718653380?text=Hi%20John%2C%20I%27d%20like%20to%20follow%20your%20forex%20trading%20insights%20and%20updates"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 py-2 bg-[#25D366] text-white font-bold text-sm hover:bg-[#1fb855] transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    FOLLOW JOHN GHATTI
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

    </div>
  );
}

export default ArticlePage;
